import { useFetcher, useParams } from "react-router";
import type { LoaderFunctionArgs } from "react-router";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import type { MembershipRequest } from "@repo/shared-types";
import { queries } from "@/lib/queries";
import { readPages } from "@/lib/query";
import { Avatar } from "@/components/avatar";
import { ShowMore, rowsOf } from "@/components/show-more";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyNote } from "@/components/empty-note";
import { UserPlus } from "lucide-react";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemFooter,
  ItemMedia,
  ItemTitle,
} from "@/components/ui/item";
import {
  SectionHeader,
  backToSpace,
  loadSection,
  useSection,
  type ActionResult,
} from "@/routes/space-section";

/**
 * The people asking to join a public space, for whoever lets them in: the
 * owner, and admins holding manage_members. An inbox line about a request
 * opens here.
 */

export async function requestsLoader({ request, params }: LoaderFunctionArgs) {
  const section = await loadSection(request, params, "requests");
  if (!section.canManageMembers) throw backToSpace(section);
  await readPages(queries.spaceRequests(params.slug ?? ""));
  return null;
}

export default function SpaceRequests() {
  const slug = useParams().slug ?? "";
  const { tenant } = useSection(slug);
  const pages = useSuspenseInfiniteQuery(queries.spaceRequests(slug));
  const requests = rowsOf(pages.data, (page) => page.requests, (request) => request.id);

  return (
    <section className="space-y-6">
      <SectionHeader tenant={tenant} title="Asking to join" />

      {requests.length === 0 ? (
        <EmptyNote icon={UserPlus}>Nobody is asking to join.</EmptyNote>
      ) : (
        <Card className="gap-0 py-1.5">
          <ul className="divide-y divide-border">
            {requests.map((request) => (
              <Request key={request.id} request={request} />
            ))}
          </ul>
        </Card>
      )}

      <ShowMore query={pages} />
    </section>
  );
}

/**
 * One person asking, answered by fetcher: the line goes as the list is read
 * again, the action's toast says which way it went, and what went wrong
 * stays on the line. On a narrow screen the buttons drop beneath the name.
 */
function Request({ request }: { request: MembershipRequest }) {
  const fetcher = useFetcher<ActionResult>();
  const busy = fetcher.state !== "idle";
  return (
    <li>
      <Item className="rounded-none">
        <ItemMedia>
          <Avatar person={{ ...request, id: request.userId }} size="md" />
        </ItemMedia>
        <ItemContent className="min-w-0 basis-32 gap-0.5">
          <ItemTitle className="block w-full truncate">{request.name || request.username}</ItemTitle>
          <ItemDescription className="truncate">@{request.username}</ItemDescription>
        </ItemContent>
        <ItemActions className="ml-auto">
          <Button
            type="button"
            size="sm"
            disabled={busy}
            onClick={() =>
              fetcher.submit({ intent: "approve", request: request.id, role: "member" }, { method: "post" })
            }
          >
            Approve
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => fetcher.submit({ intent: "decline-request", request: request.id }, { method: "post" })}
          >
            Decline
          </Button>
        </ItemActions>
        {fetcher.data?.error && (
          <ItemFooter>
            <p className="text-sm text-destructive">{fetcher.data.error}</p>
          </ItemFooter>
        )}
      </Item>
    </li>
  );
}
